import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Sparkles } from 'lucide-react';
import { api, parseApiError, type CompStage, type ApplyStandingsResponse } from '../lib/api';
import { displayClanName } from '../lib/displayName';
import { HelpText } from './HelpText';
import { StatusBadge } from './StatusBadge';
import { ResultEntryForm } from './ResultEntryForm';
import { StandingsTable } from './StandingsTable';

// ─────────────────────────────────────────────────────────────────────────────
// SeedingRoundsPanel — R2 (comp-lifecycle-pipeline): the SEEDING stage plays
// out real rounds of matches once it's LIVE with config.rounds >= 1. Rendered
// INSIDE StagePipelinePanel's SEEDING stage card, ABOVE SeedingTablePanel (the
// static ordered table those rounds are seeded FROM and, via "Apply
// standings", written BACK onto). Round 1 already generated at Start
// (existing startCompStage call) — nothing new to trigger there; this panel
// covers round DISPLAY, result entry, generating the next round, and the
// apply-standings recommendation.
//
// Result entry reuses ResultEntryForm INLINE (extracted from AdminPanel.tsx —
// it had no page-local state dependency, so this was a clean lift) rather
// than deep-linking out to Admin > Matches: an admin running seeding rounds
// stays on this one screen for the whole round-by-round loop.
// ─────────────────────────────────────────────────────────────────────────────

export function SeedingRoundsPanel({ compId, stage }: { compId: string; stage: CompStage }) {
  const qc = useQueryClient();
  const stageId = stage.stageId;
  const roundsTotal = Number(stage.config.rounds) || 0;

  const scoreboardKey = ['stageScoreboard', compId, stageId];
  const { data: scoreboard } = useQuery({
    queryKey: scoreboardKey,
    queryFn: () => api.getStageScoreboard(compId, stageId),
    enabled: !!compId && !!stageId,
  });

  // Same queryKey MatchesSection (AdminPanel.tsx) uses — shares its cache,
  // and gives this panel each match's resolvedSeries for the inline
  // ResultEntryForm (the round summary itself only carries scores/status,
  // not series shape).
  const matchesKey = ['matches', compId];
  const { data: matches } = useQuery({
    queryKey: matchesKey,
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });
  const matchById = new Map((matches ?? []).map((m) => [m.sk, m]));

  const rounds = scoreboard?.rounds ?? [];
  const [selectedRound, setSelectedRound] = useState<number | null>(null);
  const latestRound = scoreboard?.roundsPlayed ?? 0;
  const activeRound = selectedRound ?? latestRound;
  const activeRoundData = rounds.find((r) => r.round === activeRound);

  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [lastApplied, setLastApplied] = useState<ApplyStandingsResponse | null>(null);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: scoreboardKey });
    qc.invalidateQueries({ queryKey: matchesKey });
  };

  const nextRoundMut = useMutation({
    mutationFn: () => api.nextStageRound(compId, stageId),
    onSuccess: () => { setError(''); setSelectedRound(null); invalidateAll(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const applyMut = useMutation({
    mutationFn: () => api.applyStageStandings(compId, stageId),
    onSuccess: (data) => {
      setError('');
      setApplyConfirmOpen(false);
      setLastApplied(data);
      invalidateAll();
      qc.invalidateQueries({ queryKey: ['seedingTable', compId] }); // SeedingTablePanel's own cache
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!scoreboard || roundsTotal < 1) return null; // no rounds configured — legacy self-report path only

  const latestRoundData = rounds.find((r) => r.round === latestRound);
  const latestRoundComplete = !!latestRoundData
    && latestRoundData.pairings.every((p) => p.teamB === null || p.status === 'CONFIRMED');
  const roundsExhausted = latestRound >= roundsTotal;
  const nextDisabledReason = roundsExhausted
    ? `All ${roundsTotal} round(s) have been generated.`
    : !latestRoundComplete
      ? 'Confirm every match in the current round first.'
      : null;

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Seeding Rounds</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">
          Round 1 paired the seeding table 1v2, 3v4, ... (an odd team sits out with a bye). Enter each
          match's result below; once every match in a round is confirmed, generate the next round —
          it re-pairs by the current scoreboard and avoids an exact repeat where it can.
        </p>
      </HelpText>

      {/* Round tabs */}
      {rounds.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {rounds.map((r) => (
            <button
              key={r.round}
              type="button"
              onClick={() => setSelectedRound(r.round)}
              className={`rounded-md px-2.5 py-1 text-xs transition ${
                activeRound === r.round
                  ? 'bg-accent-500/15 text-accent-500'
                  : 'bg-white/[0.05] text-ink-secondary hover:bg-white/[0.08]'
              }`}
            >
              Round {r.round}
            </button>
          ))}
        </div>
      )}

      {/* Active round's matches */}
      {activeRoundData && (
        <div className="mb-3 space-y-1.5">
          {activeRoundData.pairings.map((p, i) => {
            if (p.teamB === null) {
              return (
                <div key={i} className="rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-ink-meta">
                  <span className="text-ink-secondary">{displayClanName(p.teamA)}</span> — bye this round
                </div>
              );
            }
            const match = p.matchId ? matchById.get(p.matchId) : undefined;
            const isExpanded = expandedMatchId === p.matchId;
            const confirmed = p.status === 'CONFIRMED';
            return (
              <div key={i} className="overflow-hidden rounded-lg bg-white/[0.04]">
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                    <span className="truncate text-ink-primary">{displayClanName(p.teamA)}</span>
                    {confirmed ? (
                      <span className="shrink-0 font-mono text-xs text-ink-secondary">{p.clan1Score}–{p.clan2Score}</span>
                    ) : (
                      <span className="shrink-0 text-xs text-ink-meta">vs</span>
                    )}
                    <span className="truncate text-ink-primary">{displayClanName(p.teamB)}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {p.winner && <span className="text-[11px] text-ink-secondary">🏆 {displayClanName(p.winner)}</span>}
                    {p.status && <StatusBadge status={p.status} />}
                    {p.matchId && (
                      <button
                        type="button"
                        onClick={() => setExpandedMatchId(isExpanded ? null : p.matchId!)}
                        className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-accent-500 transition-colors hover:bg-white/[0.06]"
                      >
                        <Pencil className="h-3 w-3" /> {isExpanded ? 'Cancel' : confirmed ? 'Edit result' : 'Enter result'}
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && p.matchId && (
                  <div className="border-t border-white/[0.06] px-3 py-2.5">
                    <ResultEntryForm
                      matchId={p.matchId}
                      compId={compId}
                      clan1={p.teamA}
                      clan2={p.teamB}
                      resolvedSeries={match?.resolvedSeries}
                      games={match?.games}
                      onSaved={() => { setExpandedMatchId(null); invalidateAll(); }}
                      onCancel={() => setExpandedMatchId(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Scoreboard */}
      <div className="mb-3">
        <StandingsTable standings={scoreboard.standings} />
      </div>

      {/* Generate next round */}
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!!nextDisabledReason || nextRoundMut.isPending}
          onClick={() => nextRoundMut.mutate()}
          className="btn-primary px-2.5 py-1 text-xs disabled:opacity-40"
        >
          {nextRoundMut.isPending ? 'Generating…' : `Generate round ${latestRound + 1}`}
        </button>
        {nextDisabledReason && <span className="text-[11px] text-ink-meta">{nextDisabledReason}</span>}
      </div>

      {/* Apply-standings recommendation */}
      <div className="rounded-lg bg-white/[0.03] p-2.5">
        <HelpText>
          <p className="mb-1.5 text-[11px] text-ink-meta">
            Reorders the seeding table above to match the scoreboard order shown here — a recommendation,
            not a lock. Every move is written to the seeding table's change log; you can still nudge teams
            manually afterwards.
          </p>
        </HelpText>
        {!applyConfirmOpen ? (
          <button
            type="button"
            onClick={() => setApplyConfirmOpen(true)}
            className="btn-ghost inline-flex items-center gap-1.5 px-2.5 py-1 text-xs"
          >
            <Sparkles className="h-3.5 w-3.5" /> Apply standings to seeding table
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-ink-secondary">
              Reorder to: {scoreboard.standings.map((s) => displayClanName(s.name)).join(' → ')}?
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={applyMut.isPending}
                onClick={() => applyMut.mutate()}
                className="btn-primary px-2.5 py-1 text-xs disabled:opacity-50"
              >
                {applyMut.isPending ? 'Applying…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => setApplyConfirmOpen(false)}
                className="text-xs text-ink-meta hover:text-ink-primary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {lastApplied && (
          <p className="mt-1.5 text-[11px] text-green-400">
            {lastApplied.moved === 0
              ? 'Seeding table already matched the scoreboard order — nothing moved.'
              : `Moved ${lastApplied.moved} team(s): ${lastApplied.moves.map((m) => `${displayClanName(m.clanName)} ${m.fromSeed}→${m.toSeed}`).join(', ')}`}
          </p>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
